<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <div v-if="normalizedBackMedia" class="c-unsubscribe__media">
      <AdaptiveImage
        :field="normalizedBackMedia"
        class="c-unsubscribe__media-image"
        background-position="right bottom"
        :is-background="true"
        :lazy-load="false"
        :enable-scroll-detection="false"
        :show="true"
        :show-image-redius="false"
      />
    </div>

    <ContentGrid
      :desktop-default-grid="[7, 11]"
      :tablet-default-grid="[2, 12]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-unsubscribe__panel">
        <SiteRichText
          v-if="fields.title?.value"
          class="c-unsubscribe__title"
          :field="fields.title"
        />
        <SiteRichText
          v-if="fields.description?.value"
          class="c-unsubscribe__description"
          :field="fields.description"
        />

        <form class="c-unsubscribe__form" @submit.prevent="onSubmit">
          <div class="c-unsubscribe__field-group">
            <label v-if="fields.emailLabel?.value" class="c-unsubscribe__label">
              {{ fields.emailLabel.value }}
            </label>
            <DynamicForm v-if="dynamicForm" ref="formRef" :form="dynamicForm" />
          </div>

          <div class="c-unsubscribe__field-group">
            <label v-if="fields.captchaLabel?.value" class="c-unsubscribe__label">
              {{ fields.captchaLabel.value }}
            </label>
            <div class="c-unsubscribe__captcha">
              <input
                v-model="captchaValue"
                class="c-unsubscribe__captcha-input"
                :class="{ 'has-error': Boolean(captchaError) }"
                type="text"
                inputmode="text"
                autocomplete="off"
                autocapitalize="none"
                spellcheck="false"
                aria-label="Security code"
                @input="onCaptchaInput"
              />
              <img
                class="c-unsubscribe__captcha-image"
                :class="{ 'is-loading': captchaLoading }"
                :src="captchaImage"
                alt="Security code"
                role="button"
                @click="refreshCaptcha"
              />
            </div>
            <p v-if="captchaError" class="c-unsubscribe__error">
              {{ captchaError }}
            </p>
          </div>

          <div class="c-unsubscribe__field-group">
            <label class="c-unsubscribe__label">
              {{ emailCodeLabel }}
            </label>
            <div class="c-unsubscribe__email-code">
              <input
                v-model="emailCode"
                class="c-unsubscribe__email-code-input"
                :class="{ 'has-error': Boolean(emailCodeError) }"
                type="text"
                inputmode="text"
                autocapitalize="none"
                spellcheck="false"
                autocomplete="one-time-code"
                aria-label="Email activation code"
                @input="onEmailCodeInput"
              />
              <button
                class="c-unsubscribe__send-code"
                type="button"
                :disabled="sendingCode"
                @click="onSendCode"
              >
                {{ sendingCode ? 'Sending...' : 'Send Code' }}
              </button>
            </div>
            <p v-if="emailCodeError" class="c-unsubscribe__error">
              {{ emailCodeError }}
            </p>
          </div>

          <p v-if="feedbackMessage" class="c-unsubscribe__feedback" :class="feedbackClass">
            {{ feedbackMessage }}
          </p>

          <button class="c-unsubscribe__submit" type="submit" :disabled="submitting">
            {{ submitting ? 'Submitting...' : submitLabel }}
          </button>
        </form>

        <SiteRichText
          v-if="fields.fallbackNote?.value"
          class="c-unsubscribe__note"
          :field="fields.fallbackNote"
        />
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import DynamicForm from '@/form/DynamicForm.vue';
import SiteRichText from '@/elements/SiteRichText.vue';
import api from '@/api';
import utils from '@/utils';
import type { DynamicFormRef } from '@/types/form/dynamic-form';
import type { ToastProvider } from '@/types/layout';
import type { UnsubscribeData, UnsubscribeProps } from '@/types/components/Unsubscribe';

const props = defineProps<UnsubscribeProps>();

const toast = inject<ToastProvider>('toast')!;
const formRef = ref<DynamicFormRef>();
const captchaValue = ref('');
const captchaError = ref('');
const emailCode = ref('');
const emailCodeError = ref('');
const feedbackMessage = ref('');
const feedbackType = ref<'success' | 'error'>('success');
const submitting = ref(false);
const sendingCode = ref(false);
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
let redirectTimer: ReturnType<typeof setTimeout> | undefined;

const fields = computed(() => props.fields);
const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-unsubscribe'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const submitLabel = computed(() => fields.value?.submitLabel?.value || 'Submit');
const emailCodeLabel = computed(() => 'Please enter Email activation code');
const dynamicForm = computed(() => {
  const form = fields.value?.form;
  return Array.isArray(form) ? form[0] : form;
});
const feedbackClass = computed(() => `c-unsubscribe__feedback--${feedbackType.value}`);
const normalizedBackMedia = computed(() => {
  const backMedia = fields.value?.backMedia;
  return Array.isArray(backMedia) ? backMedia[0] : backMedia;
});

const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.code_token || '';
  captchaImage.value = data.src || '';
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const getResetToken = (data: any = {}) => {
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed.reset_token || parsed.resetToken || '';
    } catch (error) {
      return '';
    }
  }

  return data.reset_token || data.resetToken || '';
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};

const onCaptchaInput = () => {
  captchaError.value = '';
  feedbackMessage.value = '';
};

const onEmailCodeInput = () => {
  emailCodeError.value = '';
  feedbackMessage.value = '';
};

const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }

  captchaError.value = 'Please enter the security code shown on the right.';
  return false;
};

const validateEmailCode = () => {
  if (emailCode.value.trim()) {
    emailCodeError.value = '';
    return true;
  }

  emailCodeError.value = 'Please enter the Email activation code.';
  return false;
};

const validateEmailForm = async () => {
  const validation = await formRef.value?.validate<UnsubscribeData>();
  if (!validation?.valid || !validation.data?.email) return null;
  return validation.data.email;
};

const onSendCode = async () => {
  if (sendingCode.value) return;

  feedbackMessage.value = '';
  const email = await validateEmailForm();
  if (!email || !validateCaptcha()) return;

  sendingCode.value = true;
  const [, error] = await api.unsubscribe.sendCode(null, {
    email,
    validateCode: captchaValue.value.trim(),
    codeToken: codeToken.value,
  });
  sendingCode.value = false;

  if (error) {
    captchaError.value = getErrorMessage(error, 'Invalid security code.');
    await refreshCaptcha();
    return;
  }

  emailCodeError.value = '';
  feedbackType.value = 'success';
  feedbackMessage.value = 'The Email activation code has been sent.';
};

const onSubmit = async () => {
  if (submitting.value) return;

  feedbackMessage.value = '';
  const email = await validateEmailForm();
  if (!email || !validateEmailCode()) return;

  submitting.value = true;
  const [verifyData, verifyError] = await api.unsubscribe.verifyCode(null, {
    email,
    verificationCode: emailCode.value.trim(),
  });

  if (verifyError) {
    submitting.value = false;
    emailCodeError.value = getErrorMessage(verifyError, 'Invalid Email activation code.');
    return;
  }

  const resetToken = getResetToken(verifyData);
  if (!resetToken) {
    submitting.value = false;
    emailCodeError.value = 'Invalid Email activation code response.';
    return;
  }

  const [, error] = await api.unsubscribe.submit(null, {
    email,
    resetToken: resetToken,
  });
  submitting.value = false;

  if (error) {
    feedbackType.value = 'error';
    feedbackMessage.value = getErrorMessage(
      error,
      fields.value?.errorMessage?.value || 'Submission failed. Please try again.'
    );
    await refreshCaptcha();
    return;
  }

  const successMessage = fields.value?.successMessage?.value
    ? fields.value.successMessage.value.replace(/<[^>]+>/g, '')
    : 'Your unsubscribe request has been submitted.';
  feedbackType.value = 'success';
  feedbackMessage.value = successMessage;
  void toast.showSuccess(successMessage, { duration: 3000 });
  clearTimeout(redirectTimer);
  redirectTimer = setTimeout(() => window.location.assign('/'), 3000);
  formRef.value?.resetFields();
  captchaValue.value = '';
  emailCode.value = '';
  await refreshCaptcha();
};
onMounted(refreshCaptcha);
onBeforeUnmount(() => clearTimeout(redirectTimer));
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-unsubscribe {
  position: relative;
  min-height: 100vh;
  padding: 76px 0 110px;
  overflow: hidden;
  background: var(--back-color, #fff);
  color: var(--color, #3f4042);

  &__media {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &__media-image {
    width: 100%;
    height: 100%;

    > div,
    .e-background-image,
    .e-background-image__img,
    .background-image {
      width: 100%;
      height: 100%;
    }

    .e-background-image__img.background-image > img,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__panel {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: 760px;
    margin: 0 auto;
  }

  &__title {
    font-size: 32px;
    font-weight: 700;
    text-align: center;
  }

  &__description {
    margin-top: 20px;
    text-align: left;
    color: #454649;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 600;
  }

  &__field-group {
    margin-top: 12px;
  }

  &__label {
    display: block;
    margin-bottom: 14px;
    color: #454649;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 600;
  }

  .c-dynamic-form__items {
    gap: 0;
  }

  .f-input {
    margin: 0;

    &__main {
      min-height: 45px;
      border: 1px solid #e1e3e1;
      border-radius: 4px;
      background: #fff;
      box-shadow: none;
    }

    &__placeholder {
      display: none;
    }

    input {
      height: 45px;
      padding: 0 14px;
      color: #3f4042;
      font-size: 14px;
      line-height: 45px;
      background: transparent;
    }
  }

  &__captcha {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 112px;
    gap: 10px;
    align-items: stretch;
  }

  &__captcha-input,
  &__email-code-input {
    width: 100%;
    height: 45px;
    padding: 0 14px;
    border: 1px solid #e1e3e1;
    border-radius: 4px;
    outline: none;
    color: #3f4042;
    font-size: 14px;
    background: #fff;

    &:focus {
      border-color: #799b80;
    }

    &.has-error {
      border-color: #b94b4b;
    }
  }

  &__email-code {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 122px;
    gap: 10px;
    align-items: stretch;
  }

  &__send-code {
    min-height: 45px;
    padding: 0 16px;
    border: 0;
    border-radius: 4px;
    background: #709777;
    color: #fff;
    font-size: 16px;
    line-height: 1;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
  }

  &__captcha-image {
    width: 112px;
    height: 45px;
    border-radius: 4px;
    object-fit: cover;
    cursor: pointer;

    &.is-loading {
      opacity: 0.55;
      pointer-events: none;
    }
  }

  &__error,
  &__feedback {
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 1.35;
  }

  &__error,
  &__feedback--error {
    color: #b94b4b;
  }

  &__feedback--success {
    color: #6f9876;
  }

  &__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 55px;
    margin-top: 27px;
    border: 0;
    border-radius: 8px;
    background: #709777;
    color: #fff;
    font-size: 18px;
    line-height: 1;
    font-weight: 700;
    cursor: pointer;
    transition:
      background 0.2s ease,
      opacity 0.2s ease;

    &:hover {
      background: #628a69;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
  }

  &__note {
    margin-top: 22px;
    color: #535456;
    font-size: 14px;
    line-height: 1.45;
    font-weight: 500;

    :where(p) {
      margin: 0;
    }
  }

  @include tablet {
    padding-top: 95px;
  }

  @include tablet-landscape {
    padding-top: 108px;

    &__panel {
      max-width: 735px;
    }
  }

  @media (max-width: 767px) {
    min-height: auto;
    padding: 58px 0 72px;

    &__label,
    &__note {
      font-size: 14px;
    }

    &__captcha,
    &__email-code {
      grid-template-columns: 1fr;
    }

    &__captcha-image {
      width: 118px;
    }

    &__submit {
      min-height: 50px;
      font-size: 16px;
    }
  }
}
</style>
